import { Element, type ThermalSpec } from './element';
import type { MnaMatrix } from './mna';
import { fmt } from './format';

export const THERMAL_V = 0.025865; // kT/q at 300K

/* ─────────────────────────── 도선 / 연결 ─────────────────────────── */

/** 저항 0인 이상 도선. 넷리스트 빌드 시 두 노드를 하나로 합친다. */
export class Wire extends Element {
  override get idealConductor(): boolean {
    return true;
  }
  info(): string[] {
    return ['도선', `전류 ${fmt(Math.abs(this.current), 'A')}`];
  }
}

/** 접지 (1핀) */
export class Ground extends Element {
  override get pinCount(): number {
    return 1;
  }
  override get isGround(): boolean {
    return true;
  }
  info(): string[] {
    return ['접지', '전압 기준점 0 V'];
  }
}

/* ─────────────────────────── 수동 소자 ─────────────────────────── */

export class Resistor extends Element {
  protected resistance(): number {
    if (this.burnt) return 1e9; // 타버리면 끊어진다
    return Math.max(this.num('resistance', 10), 1e-6);
  }
  /** 정격 전력에서 약 125℃, 정격의 2배쯤에서 타버린다 (τ ≈ 10초) */
  override thermalSpec(): ThermalSpec | null {
    const rated = Math.max(this.num('ratedPower', 0.25), 1e-3);
    const rth = 100 / rated;
    return { rth, cth: 10 / rth, tmax: 250 };
  }
  override stamp(m: MnaMatrix): void {
    m.resistor(this.nodes[0], this.nodes[1], this.resistance());
  }
  protected override computeCurrent(): number {
    return this.voltageDrop / this.resistance();
  }
  info(): string[] {
    return [
      '저항',
      `저항 ${fmt(this.num('resistance', 10), 'Ω')} (정격 ${fmt(this.num('ratedPower', 0.25), 'W')})`,
      `전압 ${fmt(this.voltageDrop, 'V')}`,
      `전류 ${fmt(this.current, 'A')}`,
      `전력 ${fmt(Math.abs(this.power), 'W')}`,
    ];
  }
}

/** 전구 — 저항과 같지만 소비전력으로 밝기를 계산한다. */
export class Lamp extends Resistor {
  /** 0~1 밝기 */
  get brightness(): number {
    if (this.burnt) return 0;
    const nominal = Math.max(this.num('nominalPower', 0.5), 1e-6);
    const ratio = Math.abs(this.power) / nominal;
    return Math.min(1, Math.pow(ratio, 0.55));
  }
  /** 필라멘트: 정격의 약 3배 전력이면 끊어진다 */
  override thermalSpec(): ThermalSpec | null {
    const nominal = Math.max(this.num('nominalPower', 0.5), 1e-3);
    const rth = 100 / nominal;
    return { rth, cth: 1 / rth, tmax: 330 };
  }
  override info(): string[] {
    return [
      '전구',
      `저항 ${fmt(this.num('resistance', 10), 'Ω')}`,
      `전압 ${fmt(this.voltageDrop, 'V')}`,
      `전류 ${fmt(this.current, 'A')}`,
      `전력 ${fmt(Math.abs(this.power), 'W')}`,
      `밝기 ${(this.brightness * 100).toFixed(0)}%`,
    ];
  }
}

/** 퓨즈 — 정격을 넘으면 끊어진다. */
export class Fuse extends Element {
  blown = false;
  override resetState(): void {
    this.blown = false;
  }
  private res(): number {
    return this.blown ? 1e9 : Math.max(this.num('resistance', 0.01), 1e-6);
  }
  override stamp(m: MnaMatrix): void {
    m.resistor(this.nodes[0], this.nodes[1], this.res());
  }
  protected override computeCurrent(): number {
    return this.voltageDrop / this.res();
  }
  override endStep(m: MnaMatrix, dt: number): void {
    super.endStep(m, dt);
    if (!this.blown && Math.abs(this.current) > this.num('rating', 1)) this.blown = true;
  }
  info(): string[] {
    return [
      '퓨즈',
      `정격 ${fmt(this.num('rating', 1), 'A')}`,
      this.blown ? '⚠ 끊어짐' : `전류 ${fmt(this.current, 'A')}`,
    ];
  }
}

const RELTOL = 2e-4;

export class Capacitor extends Element {
  private vPrev = 0;
  private geq = 0;
  private ieq = 0;
  private dvPrev = 0;
  private dtPrev = 0;

  private cap(): number {
    return Math.max(this.num('capacitance', 1e-5), 1e-15);
  }
  override resetState(): void {
    this.vPrev = this.num('initialV', 0);
    this.geq = 0;
    this.ieq = 0;
    this.dvPrev = 0;
    this.dtPrev = 0;
  }
  override beginStep(dt: number): void {
    this.geq = this.cap() / dt;
    this.ieq = this.geq * this.vPrev;
  }
  override stamp(m: MnaMatrix): void {
    const [a, b] = this.nodes;
    m.conductance(a, b, this.geq);
    m.currentSource(a, b, -this.ieq);
  }
  protected override computeCurrent(): number {
    return this.geq * this.voltageDrop - this.ieq;
  }
  override endStep(m: MnaMatrix, dt: number): void {
    super.endStep(m, dt);
    const v = this.voltageDrop;
    const dv = v - this.vPrev;
    // 국소 절단 오차 ≈ 두 번째 차분 / 2  (backward Euler)
    if (this.dtPrev > 0) {
      const lte = Math.abs(dv - (this.dvPrev * dt) / this.dtPrev) / 2;
      const tol = RELTOL * Math.max(Math.abs(v), Math.abs(this.vPrev)) + 1e-4;
      this.errRatio = lte / tol;
    } else this.errRatio = 0;
    this.dvPrev = dv;
    this.dtPrev = dt;
    this.vPrev = v;
  }
  /** 저장된 에너지 (J) */
  get energy(): number {
    return 0.5 * this.cap() * this.vPrev * this.vPrev;
  }
  info(): string[] {
    return [
      '커패시터',
      `용량 ${fmt(this.cap(), 'F')}`,
      `전압 ${fmt(this.voltageDrop, 'V')}`,
      `전류 ${fmt(this.current, 'A')}`,
      `에너지 ${fmt(this.energy, 'J')}`,
    ];
  }
}

export class Inductor extends Element {
  private iPrev = 0;
  private geq = 0;
  private diPrev = 0;
  private dtPrev = 0;

  private ind(): number {
    return Math.max(this.num('inductance', 1e-3), 1e-12);
  }
  override resetState(): void {
    this.iPrev = this.num('initialI', 0);
    this.geq = 0;
    this.diPrev = 0;
    this.dtPrev = 0;
  }
  override beginStep(dt: number): void {
    this.geq = dt / this.ind();
  }
  override stamp(m: MnaMatrix): void {
    const [a, b] = this.nodes;
    m.conductance(a, b, this.geq);
    m.currentSource(a, b, this.iPrev);
  }
  protected override computeCurrent(): number {
    return this.geq * this.voltageDrop + this.iPrev;
  }
  override endStep(m: MnaMatrix, dt: number): void {
    super.endStep(m, dt);
    const i = this.current;
    const di = i - this.iPrev;
    if (this.dtPrev > 0) {
      const lte = Math.abs(di - (this.diPrev * dt) / this.dtPrev) / 2;
      const tol = RELTOL * Math.max(Math.abs(i), Math.abs(this.iPrev)) + 1e-5;
      this.errRatio = lte / tol;
    } else this.errRatio = 0;
    this.diPrev = di;
    this.dtPrev = dt;
    this.iPrev = i;
  }
  info(): string[] {
    return [
      '인덕터',
      `인덕턴스 ${fmt(this.ind(), 'H')}`,
      `전압 ${fmt(this.voltageDrop, 'V')}`,
      `전류 ${fmt(this.current, 'A')}`,
    ];
  }
}

/* ─────────────────────────── 전원 ─────────────────────────── */

/**
 * 전지 — 노턴 등가(내부저항 병렬 전류원)로 모델링.
 * pin0 = 음극(-), pin1 = 양극(+). 방전 중이면 current > 0, power < 0.
 * capacity(mAh) > 0 이면 잔량(SoC)을 추적한다.
 */
export class Battery extends Element {
  /** 남은 전하량 (쿨롱) */
  charge = 0;

  private rint(): number {
    return Math.max(this.num('internalR', 0.05), 1e-4);
  }
  private capacityCoulombs(): number {
    return Math.max(this.num('capacity', 0), 0) * 3.6; // mAh -> C
  }
  get soc(): number {
    const cap = this.capacityCoulombs();
    if (cap <= 0) return 1;
    return Math.min(1, Math.max(0, this.charge / cap));
  }
  override resetState(): void {
    const cap = this.capacityCoulombs();
    this.charge = cap * Math.min(1, Math.max(0, this.num('initialSoc', 1)));
  }
  get isLithium(): boolean {
    return this.type === 'liion' || this.str('chemistry', 'ideal') === 'liion';
  }
  /** 개방 전압 — 잔량에 따라 떨어진다. 리튬이온은 실제 셀의 OCV 곡선을 쓴다 */
  openCircuitVoltage(): number {
    if (this.isLithium) return liionOcv(this.soc);
    const emf = this.num('emf', 1.5);
    if (this.capacityCoulombs() <= 0) return emf;
    const s = this.soc;
    if (s <= 0) return 0;
    return emf * (0.9 + 0.1 * s);
  }
  override stamp(m: MnaMatrix): void {
    const [a, b] = this.nodes;
    if (this.burnt) {
      m.conductance(a, b, 1e-9); // 열폭주 → 셀이 망가져 끊어짐
      return;
    }
    const r = this.rint();
    m.conductance(a, b, 1 / r);
    m.currentSource(a, b, this.openCircuitVoltage() / r);
  }
  protected override computeCurrent(): number {
    if (this.burnt) return this.voltageDrop * 1e-9;
    return (this.openCircuitVoltage() + this.voltageDrop) / this.rint();
  }
  /** 18650 셀 정도: 열저항 20℃/W, 열용량 50J/℃ (τ≈17분), 90℃ 넘으면 열폭주 */
  override thermalSpec(): ThermalSpec | null {
    if (!this.isLithium) return null;
    return { rth: 20, cth: 50, tmax: 90 };
  }
  /** 셀 발열은 내부저항 손실 I²R */
  override get heat(): number {
    return this.burnt ? 0 : this.current * this.current * this.rint();
  }
  override endStep(m: MnaMatrix, dt: number): void {
    super.endStep(m, dt);
    const cap = this.capacityCoulombs();
    if (cap > 0) {
      this.charge = Math.min(cap, Math.max(0, this.charge - this.current * dt));
    }
  }
  /** 단자 전압 */
  get terminalVoltage(): number {
    return -this.voltageDrop;
  }
  info(): string[] {
    const lines = [
      '전지',
      `기전력 ${fmt(this.num('emf', 1.5), 'V')}`,
      `내부저항 ${fmt(this.rint(), 'Ω')}`,
      `단자전압 ${fmt(this.terminalVoltage, 'V')}`,
      `전류 ${fmt(this.current, 'A')} (${this.current >= 0 ? '방전' : '충전'})`,
    ];
    if (this.capacityCoulombs() > 0) {
      lines.push(`잔량 ${(this.soc * 100).toFixed(1)}% / ${fmt(this.num('capacity', 0) / 1000, 'Ah')}`);
      lines.push(`남은 에너지 ${fmt((this.charge / 3600) * this.openCircuitVoltage(), 'Wh')}`);
    }
    if (this.burnt) lines.push('⚠ 열폭주! 셀이 망가졌어요');
    if (this.isLithium) {
      lines[0] = '리튬이온 셀';
      lines[1] = `개방전압 ${fmt(this.openCircuitVoltage(), 'V')}`;
    }
    return lines;
  }
}

/** USB 전원 — 내부저항 있는 5V 전원. 전지와 같은 모델 */
export class UsbSource extends Battery {
  override info(): string[] {
    const lines = super.info();
    lines[0] = 'USB 전원';
    return lines;
  }
}

/** 리튬이온 셀 개방전압 (SoC 0~1 → V). 일반적인 NMC 셀 곡선 근사 */
const LIION_OCV: [number, number][] = [
  [0, 2.5], [0.02, 3.1], [0.05, 3.35], [0.1, 3.5], [0.2, 3.62], [0.3, 3.69],
  [0.4, 3.74], [0.5, 3.79], [0.6, 3.85], [0.7, 3.92], [0.8, 4.0], [0.9, 4.08], [1, 4.2],
];
export function liionOcv(soc: number): number {
  const s = Math.min(1, Math.max(0, soc));
  for (let i = 1; i < LIION_OCV.length; i++) {
    const [s1, v1] = LIION_OCV[i];
    if (s <= s1) {
      const [s0, v0] = LIION_OCV[i - 1];
      return v0 + ((v1 - v0) * (s - s0)) / (s1 - s0);
    }
  }
  return 4.2;
}

export type Waveform = 'dc' | 'sine' | 'square' | 'triangle';

/** 이상 전압원 (DC / AC). pin0 = -, pin1 = + */
export class VoltageSource extends Element {
  private time = 0;
  override get voltageSourceCount(): number {
    return 1;
  }
  override resetState(): void {
    this.time = 0;
  }
  override beginStep(_dt: number, time: number): void {
    this.time = time;
  }
  override get maxStep(): number {
    if (this.str('waveform', 'dc') === 'dc') return Infinity;
    return 1 / (Math.max(this.num('frequency', 60), 1e-6) * 40);
  }
  value(): number {
    const amp = this.num('voltage', 5);
    const wave = this.str('waveform', 'dc') as Waveform;
    if (wave === 'dc') return amp;
    const f = Math.max(this.num('frequency', 60), 1e-9);
    const offset = this.num('offset', 0);
    const phase = 2 * Math.PI * f * this.time;
    switch (wave) {
      case 'sine':
        return offset + amp * Math.sin(phase);
      case 'square':
        return offset + amp * (Math.sin(phase) >= 0 ? 1 : -1);
      case 'triangle': {
        const t = ((f * this.time) % 1 + 1) % 1;
        return offset + amp * (t < 0.5 ? 4 * t - 1 : 3 - 4 * t);
      }
      default:
        return amp;
    }
  }
  override stamp(m: MnaMatrix): void {
    // V(+) - V(-) = value
    m.voltageSource(this.nodes[1], this.nodes[0], this.vsIndex, this.value());
  }
  protected override computeCurrent(m: MnaMatrix): number {
    return -m.branchCurrent(this.vsIndex);
  }
  info(): string[] {
    return [
      '전압원',
      `파형 ${this.str('waveform', 'dc')}`,
      `전압 ${fmt(this.value(), 'V')}`,
      `전류 ${fmt(this.current, 'A')}`,
    ];
  }
}

/** 이상 전류원. pin0 -> pin1 방향으로 흐른다. */
export class CurrentSource extends Element {
  override stamp(m: MnaMatrix): void {
    m.currentSource(this.nodes[0], this.nodes[1], this.num('amps', 0.01));
  }
  protected override computeCurrent(): number {
    return this.num('amps', 0.01);
  }
  info(): string[] {
    return ['전류원', `전류 ${fmt(this.num('amps', 0.01), 'A')}`, `전압 ${fmt(this.voltageDrop, 'V')}`];
  }
}

/* ─────────────────────────── 스위치 / 계측기 ─────────────────────────── */

export class Switch extends Element {
  override get idealConductor(): boolean {
    return this.bool('closed', false);
  }
  override stamp(m: MnaMatrix): void {
    if (!this.idealConductor) m.resistor(this.nodes[0], this.nodes[1], 1e9);
  }
  protected override computeCurrent(): number {
    return this.idealConductor ? this.current : this.voltageDrop / 1e9;
  }
  toggle(): void {
    this.props.closed = !this.bool('closed', false);
  }
  info(): string[] {
    return ['스위치', this.bool('closed', false) ? '닫힘 (ON)' : '열림 (OFF)', `전류 ${fmt(this.current, 'A')}`];
  }
}

/** 전압계 — 매우 큰 내부저항. pin0 = +, pin1 = - */
export class Voltmeter extends Element {
  private res(): number {
    return Math.max(this.num('resistance', 1e8), 1e3);
  }
  override stamp(m: MnaMatrix): void {
    m.resistor(this.nodes[0], this.nodes[1], this.res());
  }
  protected override computeCurrent(): number {
    return this.voltageDrop / this.res();
  }
  get reading(): number {
    return this.voltageDrop;
  }
  info(): string[] {
    return ['전압계', `측정값 ${fmt(this.reading, 'V')}`, `내부저항 ${fmt(this.res(), 'Ω')}`];
  }
}

/** 전류계 — 이상적으로 저항 0. 전류는 도선 흐름 계산에서 얻는다. */
export class Ammeter extends Element {
  override get idealConductor(): boolean {
    return true;
  }
  get reading(): number {
    return this.current;
  }
  info(): string[] {
    return ['전류계', `측정값 ${fmt(this.reading, 'A')}`];
  }
}

/* ─────────────────────────── 비선형 소자 ─────────────────────────── */

export class Diode extends Element {
  protected vLast = 0;

  protected satCurrent(): number {
    return Math.max(this.num('is', 1e-14), 1e-20);
  }
  protected emission(): number {
    return Math.max(this.num('n', 1), 0.1);
  }
  override get nonlinear(): boolean {
    return true;
  }
  override resetState(): void {
    this.vLast = 0;
  }
  private vt(): number {
    return THERMAL_V * this.emission();
  }
  override thermalSpec(): ThermalSpec | null {
    return { rth: 300, cth: 2 / 300, tmax: 175 };
  }
  override stamp(m: MnaMatrix): void {
    const [a, b] = this.nodes;
    if (this.burnt) {
      m.conductance(a, b, 1e-12);
      return;
    }
    const vt = this.vt();
    const is = this.satCurrent();
    const v = this.vLast;
    const ex = Math.exp(Math.min(v / vt, 60));
    const i = is * (ex - 1);
    const g = Math.max((is / vt) * ex, 1e-12);
    m.conductance(a, b, g);
    m.currentSource(a, b, i - g * v);
  }
  /** 뉴턴 반복: 새 해를 읽어 동작점을 갱신하고 변화량을 돌려준다 */
  override updateNewton(m: MnaMatrix): number {
    if (this.burnt) return 0;
    const vNew = m.nodeVoltage(this.nodes[0]) - m.nodeVoltage(this.nodes[1]);
    const vt = this.vt();
    const vcrit = vt * Math.log(vt / (Math.SQRT2 * this.satCurrent()));
    const limited = limitJunction(vNew, this.vLast, vt, vcrit);
    const delta = Math.abs(limited - this.vLast);
    this.vLast = limited;
    return delta;
  }
  protected override computeCurrent(): number {
    if (this.burnt) return this.voltageDrop * 1e-12;
    const vt = this.vt();
    return this.satCurrent() * (Math.exp(Math.min(this.voltageDrop / vt, 60)) - 1);
  }
  info(): string[] {
    return ['다이오드', `전압 ${fmt(this.voltageDrop, 'V')}`, `전류 ${fmt(this.current, 'A')}`];
  }
}

export class Led extends Diode {
  protected override emission(): number {
    return 2;
  }
  protected override satCurrent(): number {
    // 순방향 전압 vf 에서 20mA 가 흐르도록 Is 를 역산
    const vf = Math.max(this.num('vf', 2.0), 0.3);
    return 0.02 * Math.exp(-vf / (THERMAL_V * 2));
  }
  get brightness(): number {
    if (this.burnt) return 0;
    const nominal = Math.max(this.num('nominalI', 0.02), 1e-6);
    return Math.min(1, Math.pow(Math.max(this.current, 0) / nominal, 0.6));
  }
  /** 5mm LED: 20mA 에서 +12℃ 정도, 약 0.33W(≈150mA) 넘으면 탄다 */
  override thermalSpec(): ThermalSpec | null {
    return { rth: 300, cth: 1 / 300, tmax: 125 };
  }
  override info(): string[] {
    return [
      'LED',
      `순방향전압 ${fmt(this.num('vf', 2), 'V')}`,
      `전압 ${fmt(this.voltageDrop, 'V')}`,
      `전류 ${fmt(this.current, 'A')}`,
      `밝기 ${(this.brightness * 100).toFixed(0)}%`,
    ];
  }
}

/** SPICE 의 pnjlim — 지수함수 발산을 막는 전압 제한 */
export function limitJunction(vNew: number, vOld: number, vt: number, vcrit: number): number {
  if (vNew > vcrit && Math.abs(vNew - vOld) > 2 * vt) {
    if (vOld > 0) {
      const arg = 1 + (vNew - vOld) / vt;
      return arg > 0 ? vOld + vt * Math.log(arg) : vcrit;
    }
    return vt * Math.log(Math.max(vNew / vt, 1e-9));
  }
  if (vNew < -5) return Math.max(vNew, vOld - 5);
  return vNew;
}
